import { Clone } from "@react-three/drei";
import React, { useEffect, useLayoutEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { createDissolveMaterial2 } from "./materials/DissolveMaterial/DissolveMaterial";
import { useSpring } from "@react-spring/three";
import { useObjectCallback, useSheet } from "../../hooks/useTheatre";
import { types } from "@theatre/core";

const DissolveAnimationConfig = {
  dissolveValue: types.number(0, { range: [0, 1] }),
  lineOpacity: types.number(0, { range: [0, 1] }),
};

const DissolvableMesh = ({ children, dissolved = false, lineOffsetPosition = new THREE.Vector3(), name, ...props }) => {
  const groupRef = useRef();

  const materials = useRef([]);

  const lines = useRef([]);
  const lineRootRef = useRef();

  const sheet = useSheet("EFD", "DissolvableMesh", name);

  const replaceMaterial = (obj) => {
    if (obj.material.__csm) return;
    // console.log(obj.material.__csm);
    // if(obj.material)
    let newMat = createDissolveMaterial2(obj.material);
    obj.material = newMat;
    materials.current.push(newMat);
  };

  const addWireframe = (target) => {
    let edges = new THREE.EdgesGeometry(target.geometry.clone(), 25);

    const mat = new THREE.LineBasicMaterial({
      color: new THREE.Color("#082e1a").multiplyScalar(125),
      opacity: 0,
      depthTest: false,
      depthWrite: false,
      transparent: true,
      toneMapped: false,
      blending: THREE.AdditiveBlending,
      stencilWrite: true,
      stencilFunc: THREE.AlwaysStencilFunc,
    });
    let line = new THREE.LineSegments(edges, mat);
    line.computeLineDistances();
    line.renderOrder = 3;
    line.visible = true;

    const worldpos = new THREE.Vector3();
    target.getWorldPosition(worldpos);
    const worldRotation = new THREE.Quaternion();
    target.getWorldQuaternion(worldRotation);
    line.position.copy(worldpos);
    line.position.sub(lineOffsetPosition);
    line.scale.copy(target.scale);
    line.quaternion.copy(worldRotation);

    lineRootRef.current.add(line);

    lines.current.push(line);
  };

  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.traverse((obj) => {
        if (obj.isMesh) {
          replaceMaterial(obj);
          addWireframe(obj);
        }
      });
    }
  }, [groupRef.current]);

  const setLineOpacity = (v) => {
    lines.current.forEach((l) => {
      if (l.material instanceof THREE.LineBasicMaterial) {
        l.material.opacity = v;
      }
    });
  };

  useObjectCallback(sheet, "Animation", DissolveAnimationConfig, ({ dissolveValue, lineOpacity }) => {
    materials.current.forEach((m) => {
      // console.log(m.uniforms);
      m.uniforms.uDissolve.value = dissolveValue;
    });
    setLineOpacity(lineOpacity);
  });

  useEffect(() => {
    if (dissolved) {
      sheet.sequence.position = 0;
      sheet.sequence.play({ iterationCount: 1 });
    } else {
      sheet.sequence.play({ iterationCount: 1, direction: "reverse" });
    }
  }, [dissolved]);

  return (
    <group>
      <group {...props} ref={groupRef}>
        {children}
        <group ref={lineRootRef}></group>
      </group>
    </group>
  );
};

export default DissolvableMesh;
